import { useId, useState, type ReactNode } from "react";
import { InfoIcon } from "@client/components/icons";

const BUBBLE =
  "absolute top-full z-40 mt-1.5 w-56 max-w-[min(14rem,70vw)] rounded-lg border border-line bg-surface-strong px-2.5 py-2 text-xs font-normal leading-snug text-ink shadow-lg";

interface TooltipProps {
  content: ReactNode;
  /** The thing being explained. Anything focusable or hoverable. */
  children: ReactNode;
  /**
   * Given when a control points `aria-describedby` at this text. The bubble
   * then keeps the id and stays in the DOM while closed, `sr-only`. Left out
   * when the trigger's own accessible name already says the same thing — the
   * bubble is then decorative and hidden from assistive tech entirely.
   */
  id?: string;
  /** Which edge the bubble hangs from; `end` when the trigger is right-aligned. */
  align?: "start" | "end";
  /**
   * What a click does. `toggle` for a trigger whose only job is to reveal the
   * text, so it works on a touch screen with no hover. `close` — the default —
   * for a trigger that does something else on click and should not leave its
   * own tooltip hanging over the result.
   */
  onClick?: "toggle" | "close";
  /** Applied to the positioning wrapper, which is the layout box the parent sees. */
  className?: string;
}

/**
 * The bubble, on hover *and* on focus.
 *
 * A tooltip only a mouse can reach is not a tooltip, so both open it and
 * `Escape` dismisses it. There is exactly **one** copy of the text in the DOM —
 * the same element, `sr-only` while closed and the bubble while open — because
 * a hidden copy for `aria-describedby` plus a visible copy for everyone else
 * reads the sentence twice the moment the bubble opens.
 */
export function Tooltip({
  content,
  children,
  id,
  align = "start",
  onClick = "close",
  className = "relative inline-flex",
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  // Described: the bubble is the accessible description and must stay put.
  // Decorative: the trigger's name already says it, so the bubble is visual only.
  const described = id !== undefined;

  return (
    <span
      className={className}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      // `onFocus`/`onBlur` in React are focusin/focusout, so they fire for the
      // trigger inside without the trigger having to know about any of this.
      //
      // Gated on `:focus-visible` so that only *keyboard* focus opens the
      // bubble. A dialog handing focus back to the rail button that opened it
      // is focus nobody asked for, and a tooltip appearing on its own the
      // moment a dialog closes reads as a glitch.
      onFocus={(event) => {
        try {
          if (!event.target.matches(":focus-visible")) return;
        } catch {
          // Engines without `:focus-visible` (jsdom among them) throw on the
          // selector; opening is the safer miss of the two.
        }
        setOpen(true);
      }}
      onBlur={() => setOpen(false)}
      onClick={() => setOpen((current) => (onClick === "toggle" ? !current : false))}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          // Stopped so a tip inside a dialog does not also close the dialog.
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      {children}
      <span
        id={id}
        role={described ? "tooltip" : undefined}
        aria-hidden={described ? undefined : true}
        className={
          open
            ? `${BUBBLE} ${align === "end" ? "right-0" : "left-0"}`
            : described
              ? "sr-only"
              : "hidden"
        }
      >
        {content}
      </span>
    </span>
  );
}

interface InfoTipProps {
  /**
   * What the tip is about, used to build the trigger's accessible name. A
   * screen reader hears "More about Horizon", not twelve identical buttons.
   */
  label: string;
  children: ReactNode;
  /** Supplied when a field already points `aria-describedby` at this text. */
  id?: string;
  align?: "start" | "end";
}

/**
 * An icon that carries the sentence that used to sit under the control.
 *
 * The editor explained every input in prose, which is the right thing to say
 * and the wrong amount of it to have on screen at once — four panels of
 * two-line hints is most of the vertical scroll this redesign removed. The
 * words are unchanged; they are just folded behind an icon.
 */
export function InfoTip({ label, children, id, align = "start" }: InfoTipProps) {
  const generated = useId();
  const tipId = id ?? generated;

  return (
    <Tooltip content={children} id={tipId} align={align} onClick="toggle">
      <button
        type="button"
        aria-label={`More about ${label}`}
        aria-describedby={tipId}
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-ink-soft transition-colors hover:text-azure-deep"
      >
        <InfoIcon className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}
